import React from 'react';
import { Box, Paper } from '@mui/material';
import { ContentBlock, ContentComponentProps } from '../../services/contentManager';

// Legacy support for old HTML content blocks
export interface HTMLContentBlock {
  id: string;
  title?: string;
  htmlContent: string;
  keywords?: string[];
  description?: string;
}

interface HTMLContentDisplayProps {
  content: ContentBlock | HTMLContentBlock;
  sx?: any;
  candidate?: any;
  candidateId?: string;
  onContentChange?: (contentId: string) => void;
  onActionClick?: (action: string, data?: any) => void;
}

// Type guard to check if content is the new ContentBlock format
const isContentBlock = (content: ContentBlock | HTMLContentBlock): content is ContentBlock => {
  return 'component' in content;
};

const HTMLContentDisplay: React.FC<HTMLContentDisplayProps> = ({ 
  content, 
  sx,
  candidate,
  candidateId,
  onContentChange, 
  onActionClick 
}) => {
  
  // Handle clicks on elements with data attributes (for legacy HTML content)
  const handleClick = (event: React.MouseEvent) => {
    const target = event.target as HTMLElement;
    const contentId = target.getAttribute('data-content-id');
    const action = target.getAttribute('data-action');
    const actionData = target.getAttribute('data-action-data');
    
    if (contentId && onContentChange) {
      onContentChange(contentId);
    } else if (action && onActionClick) {
      onActionClick(action, actionData ? JSON.parse(actionData) : undefined);
    }
  };

  // Render new React component-based content
  if (isContentBlock(content)) {
    const ContentComponent = content.component;
    
    return (
      <Paper
        elevation={0}
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          bgcolor: 'var(--white)',
          ...sx,
        }}
      >
        <Box
          sx={{
            flex: 1,
            overflow: 'auto',
            background: 'var(--light-gray)',
            p: 3,
          }}
        >
          <ContentComponent 
            candidate={candidate}
            candidateId={candidateId}
            onContentChange={onContentChange} 
            onActionClick={onActionClick}
          />
        </Box>
      </Paper>
    );
  }

  // Fallback: Render legacy HTML content
  return (
    <Paper
      elevation={0}
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        bgcolor: 'var(--white)',
        ...sx,
      }}
    >
      <Box
        sx={{
          flex: 1,
          overflow: 'auto',
          background: 'var(--light-gray)',
          p: 2,
        }}
      >
        <Box
          onClick={handleClick}
          sx={{
            '& h1, & h2, & h3, & h4, & h5, & h6': {
              fontFamily: 'var(--font-heading)',
              color: 'var(--primary-blue)',
              marginBottom: '16px',
            },
            '& p': {
              fontFamily: 'var(--font-body)',
              color: 'var(--dark-gray)',
              lineHeight: 1.6,
              marginBottom: '12px',
            },
            '& .capability-card': {
              background: 'var(--white)',
              border: '1px solid #e0e7ff',
              borderRadius: '8px',
              padding: '20px',
              margin: '16px 0',
              boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
              transition: 'all 0.3s ease',
              '&:hover': {
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                transform: 'translateY(-2px)',
              },
            },
            '& .capability-icon': {
              fontSize: '24px',
              marginRight: '12px',
              color: 'var(--primary-blue)',
            },
            '& .capability-title': {
              fontFamily: 'var(--font-heading)',
              fontSize: '18px',
              fontWeight: 600,
              color: 'var(--primary-blue)',
              marginBottom: '8px',
            },
            '& .capability-description': {
              fontFamily: 'var(--font-body)',
              fontSize: '14px',
              color: 'var(--dark-gray)',
              lineHeight: 1.5,
            },
            '& .grid-container': {
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
              gap: '16px',
              padding: '16px 0',
            },
            '& .feature-list': {
              listStyle: 'none',
              padding: 0,
              '& li': {
                background: 'var(--white)',
                border: '1px solid #e0e7ff',
                borderRadius: '6px',
                padding: '12px 16px',
                margin: '8px 0',
                display: 'flex',
                alignItems: 'center',
                '&:before': {
                  content: '"✓"',
                  color: 'var(--primary-blue)',
                  fontWeight: 'bold',
                  marginRight: '12px',
                },
              },
            },
            '& .stats-container': {
              display: 'flex',
              justifyContent: 'space-around',
              flexWrap: 'wrap',
              gap: '16px',
              margin: '20px 0',
            },
            '& .stat-item': {
              textAlign: 'center',
              background: 'var(--white)',
              borderRadius: '8px',
              padding: '16px',
              minWidth: '120px',
              border: '1px solid #e0e7ff',
            },
            '& .stat-number': {
              fontSize: '24px',
              fontWeight: 'bold',
              color: 'var(--primary-blue)',
              display: 'block',
            },
            '& .stat-label': {
              fontSize: '12px',
              color: 'var(--dark-gray)',
              marginTop: '4px',
            },
            '& .action-button': {
              background: 'var(--primary-blue)',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              padding: '12px 24px',
              fontSize: '14px',
              fontWeight: 600,
              cursor: 'pointer',
              margin: '8px 4px',
              transition: 'all 0.3s ease',
              '&:hover': {
                background: 'var(--primary-blue-dark)',
                transform: 'translateY(-1px)',
              },
            },
            '& .highlight-box': {
              background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
              border: '1px solid var(--primary-blue)',
              borderRadius: '8px',
              padding: '20px',
              margin: '16px 0',
              borderLeft: '4px solid var(--primary-blue)',
            },
          }}
          dangerouslySetInnerHTML={{ __html: content.htmlContent }}
        />
      </Box>
    </Paper>
  );
};

export default HTMLContentDisplay;
